import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Alert } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import TextField from '@/components/TextField/TextField';
import Select from '@/components/Select/Select';
import Button from '@/components/Button/Button';
import DropZoneBig from '@/components/DropZoneBig/DropZoneBig';
import { useAuthContext } from '@/contexts/AuthContext';
import useApiErrorHandler from '@/hooks/useApiErrorHandler';
import { postFeedback, PostFeedbackParams } from '@/lib/api';
import { ApiError } from '@/lib/types';
import EditPencilIcon from '@/assets/edit-pencil.svg?react';
import CheckCircleIcon from '@/assets/check-circle.svg?react';
import TrashIcon from '@/assets/trash.svg?react';

export type FeedbackType =
  | 'general_feedback'
  | 'feature_request'
  | 'report_an_issue'
  | 'satisfaction_survey';

type FormErrors = {
  feedback_type?: string;
  message?: string;
  title?: string;
  name?: string;
  email?: string;
};

type ViewState = 'form' | 'success';

const FEEDBACK_TYPE_OPTIONS: {
  id: FeedbackType;
  value: FeedbackType;
  label: string;
  text: string;
}[] = [
  {
    id: 'general_feedback',
    value: 'general_feedback',
    label: 'General Feedback',
    text: 'General Feedback',
  },
  {
    id: 'feature_request',
    value: 'feature_request',
    label: 'Feature Request',
    text: 'Feature Request',
  },
  {
    id: 'report_an_issue',
    value: 'report_an_issue',
    label: 'Report an Issue',
    text: 'Report an Issue',
  },
  {
    id: 'satisfaction_survey',
    value: 'satisfaction_survey',
    label: 'Satisfaction Survey',
    text: 'Satisfaction Survey',
  },
];

const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

type FeedbackFormProps = {
  variant?: 'card' | 'plain';
  isActive?: boolean;
  onClose?: () => void;
};

const FeedbackForm = ({
  variant = 'card',
  isActive = true,
  onClose,
}: FeedbackFormProps) => {
  const { user } = useAuthContext();
  const [view, setView] = useState<ViewState>('form');
  const [submissionError, setSubmissionError] = useState<
    string | null
  >(null);
  const [feedbackType, setFeedbackType] =
    useState<FeedbackType | null>(null);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const { handleError } = useApiErrorHandler();

  const isBugReport = feedbackType === 'report_an_issue';

  const { mutate: submitFeedback, isPending: isSubmitting } =
    useMutation<unknown, AxiosError<ApiError>, PostFeedbackParams>({
      mutationFn: postFeedback,
      onSuccess: () => {
        setView('success');
      },
      onError: (error) => {
        const { fieldErrors, generalErrors, unhandledErrors } =
          handleError(error);

        setFormErrors(fieldErrors as FormErrors);
        setSubmissionError(
          [...generalErrors, ...unhandledErrors].join(' ') ||
            'Something went wrong while submitting your feedback. Please try again.',
        );
      },
    });

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const resetForm = () => {
    setFeedbackType(null);
    setMessage('');
    setTitle('');
    setAttachments([]);
    setFormErrors({});
    setSubmissionError(null);
    setName(user?.name || '');
    setEmail(user?.email || '');
    setView('form');
  };

  useEffect(() => {
    if (!isActive) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleFeedbackTypeChange = (value: FeedbackType | null) => {
    setFeedbackType(value);
    if (value !== 'report_an_issue') {
      setTitle('');
      setAttachments([]);
    }
    if (formErrors.feedback_type) {
      setFormErrors((prev) => {
        const {
          feedback_type: _feedback_type,
          title: _title,
          ...rest
        } = prev;
        return rest;
      });
    }
  };

  const handleFilesAdded = (files: File[]) => {
    if (!files.length) return;
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!feedbackType) {
      errors.feedback_type = 'Feedback type is required';
    }

    if (!message.trim()) {
      errors.message = 'Feedback message is required';
    }

    if (isBugReport && !title.trim()) {
      errors.title = 'Title is required for bug reports';
    }

    if (email.trim() && !validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setSubmissionError(null);

    submitFeedback({
      feedback_type: feedbackType as FeedbackType,
      message,
      name: name.trim() || null,
      email: email.trim() || null,
      title: title.trim() || null,
      attachments,
    });
  };

  const handleSubmitAnother = () => {
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  if (view === 'success') {
    return (
      <ContentWrapper $variant={variant}>
        <SuccessContent>
          <SuccessIcon aria-hidden />
          <SuccessTitle>
            <FormattedMessage
              id="feedbackWidget.successTitle"
              defaultMessage="Thank you for your feedback"
            />
          </SuccessTitle>
          <SuccessDescription>
            <FormattedMessage
              id="feedbackWidget.successDescription"
              defaultMessage="Your feedback has been successfully submitted."
            />
          </SuccessDescription>
          <SuccessActions>
            <Button
              variant="contained"
              onClick={handleSubmitAnother}
              aria-label="Submit Another Feedback Button"
            >
              <FormattedMessage
                id="feedbackWidget.submitAnother"
                defaultMessage="Submit Another Feedback"
              />
            </Button>
            <Button
              variant="text"
              onClick={handleClose}
              aria-label="Close Feedback Button"
            >
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          </SuccessActions>
        </SuccessContent>
      </ContentWrapper>
    );
  }

  const formContent = (
    <>
      {variant === 'card' && (
        <Header>
          <EditPencilIcon aria-hidden />
          <HeaderText>
            <Title>
              <FormattedMessage
                id="feedbackWidget.title"
                defaultMessage="Share your Feedback"
              />
            </Title>
            <Description>
              <FormattedMessage
                id="feedbackWidget.description"
                defaultMessage="Report issues, request features, or share general feedback without leaving the portal."
              />
            </Description>
          </HeaderText>
        </Header>
      )}

      {variant === 'plain' && (
        <PlainDescription>
          <FormattedMessage
            id="feedbackWidget.description"
            defaultMessage="Report issues, request features, or share general feedback without leaving the portal."
          />
        </PlainDescription>
      )}

      {submissionError && (
        <StyledAlert severity="error">{submissionError}</StyledAlert>
      )}

      <Form onSubmit={(e) => e.preventDefault()}>
        <FieldGroup>
          <Label>
            <FormattedMessage
              id="feedbackWidget.feedbackType"
              defaultMessage="Feedback Type"
            />
            <Asterisk>*</Asterisk>
          </Label>
          <Select
            value={feedbackType}
            onChange={(value) =>
              handleFeedbackTypeChange(value as FeedbackType | null)
            }
            options={FEEDBACK_TYPE_OPTIONS}
            placeholder={
              <FormattedMessage
                id="feedbackWidget.feedbackTypePlaceholder"
                defaultMessage="Select feedback type"
              />
            }
            aria-label="Feedback Type"
          />
          {formErrors.feedback_type && (
            <FieldError>{formErrors.feedback_type}</FieldError>
          )}
        </FieldGroup>

        {isBugReport && (
          <FieldGroup>
            <Label>
              <FormattedMessage
                id="feedbackWidget.issueTitle"
                defaultMessage="Title"
              />
              <Asterisk>*</Asterisk>
            </Label>
            <StyledTextField
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (formErrors.title) {
                  setFormErrors((prev) => {
                    const { title: _title, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              placeholder="Brief summary of the issue"
              variant="outlined"
              fullWidth
              error={!!formErrors.title}
              helperText={formErrors.title || ' '}
              aria-label="Issue Title"
            />
          </FieldGroup>
        )}

        <FieldGroup>
          <Label>
            <FormattedMessage
              id="feedbackWidget.message"
              defaultMessage="Feedback Message"
            />
            <Asterisk>*</Asterisk>
          </Label>
          <StyledTextField
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (formErrors.message) {
                setFormErrors((prev) => {
                  const { message: _message, ...rest } = prev;
                  return rest;
                });
              }
            }}
            placeholder="Tell us what's on your mind..."
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            error={!!formErrors.message}
            helperText={formErrors.message || ' '}
            aria-label="Feedback Message"
          />
        </FieldGroup>

        {isBugReport && (
          <FieldGroup>
            <Label>
              <FormattedMessage
                id="feedbackWidget.attachments"
                defaultMessage="Attachments"
              />
            </Label>
            <DropZoneBig
              busy={isSubmitting}
              onFilesChange={(picked) =>
                handleFilesAdded(picked.map(({ file }) => file))
              }
            >
              {attachments.length > 0 && (
                <AttachmentList>
                  {attachments.map((file, index) => (
                    <AttachmentItem key={`${file.name}-${index}`}>
                      <span>{file.name}</span>
                      <RemoveAttachmentButton
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <TrashIcon aria-hidden />
                      </RemoveAttachmentButton>
                    </AttachmentItem>
                  ))}
                </AttachmentList>
              )}
            </DropZoneBig>
          </FieldGroup>
        )}

        <ContactSection>
          <ContactSectionTitle>
            <FormattedMessage
              id="feedbackWidget.contactInfo"
              defaultMessage="Contact Information"
            />
          </ContactSectionTitle>
          <ContactFields>
            <FieldGroup>
              <Label>
                <FormattedMessage id="name" defaultMessage="Name" />
              </Label>
              <StyledTextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                variant="outlined"
                fullWidth
                aria-label="Name"
              />
            </FieldGroup>
            <FieldGroup>
              <Label>
                <FormattedMessage id="email" defaultMessage="Email" />
              </Label>
              <StyledTextField
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (formErrors.email) {
                    setFormErrors((prev) => {
                      const { email: _email, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="your.email@example.com"
                variant="outlined"
                fullWidth
                type="email"
                error={!!formErrors.email}
                helperText={formErrors.email || ' '}
                aria-label="Email"
              />
            </FieldGroup>
          </ContactFields>
        </ContactSection>

        <SubmitButton
          type="button"
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
          aria-label="Submit Feedback Button"
        >
          <FormattedMessage id="submit" defaultMessage="Submit" />
        </SubmitButton>
      </Form>
    </>
  );

  return (
    <ContentWrapper $variant={variant}>{formContent}</ContentWrapper>
  );
};

const ContentWrapper = styled('div')<{ $variant: 'card' | 'plain' }>(
  ({ theme, $variant }) =>
    $variant === 'card'
      ? {
          backgroundColor: theme.palette.white,
          borderRadius: theme.radii.xs,
          padding: theme.spacing(3, 4),
          border: `1px solid ${theme.palette.grey200}`,
        }
      : {},
);

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),

  '> svg': {
    width: 24,
    height: 24,
    flexShrink: 0,
    marginTop: 2,

    path: {
      stroke: theme.palette.primary.main,
    },
  },
}));

const HeaderText = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const Title = styled('h2')(({ theme }) => ({
  margin: 0,
  fontSize: 18,
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
}));

const Description = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
}));

const PlainDescription = styled('p')(({ theme }) => ({
  margin: `0 0 ${theme.spacing(2)}`,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
}));

const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const FieldGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const Label = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.textPrimaryBlack,
}));

const Asterisk = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  marginLeft: 2,
}));

const StyledTextField = styled(TextField)({
  marginTop: 0,
});

const FieldError = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  fontSize: 12,
  lineHeight: '16px',
  marginTop: theme.spacing(0.5),
}));

const ContactSection = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const ContactSectionTitle = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
  marginBottom: theme.spacing(1.5),
  color: theme.palette.textPrimaryBlack,
}));

const ContactFields = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const AttachmentList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: `${theme.spacing(1)} 0 0`,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const AttachmentItem = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.radii.xs,
  backgroundColor: theme.palette.grey100,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textPrimaryBlack,
}));

const RemoveAttachmentButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.5),
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: theme.radii.xxs,

  '> svg': {
    width: 16,
    height: 16,

    path: {
      stroke: theme.palette.grey600,
    },
  },

  '&:hover': {
    backgroundColor: theme.palette.grey200,
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  alignSelf: 'flex-end',
  marginTop: theme.spacing(1),
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SuccessContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  gap: theme.spacing(1.5),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  width: 48,
  height: 48,

  path: {
    stroke: theme.palette.green500,
  },
}));

const SuccessTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  fontSize: 20,
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
}));

const SuccessDescription = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
}));

const SuccessActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'center',
}));

export default FeedbackForm;
